/**
 * "You may also like" carousel (sections/product-recommendations.liquid).
 *
 * The row is a native horizontal scroller with scroll-snap, so touch swipes and sideways
 * trackpad scrolls work without any script. This element adds:
 * - left/right arrows that step one card and wrap around at either end (like the homepage
 *   Featured products carousel, which also loops);
 * - mouse dragging, which settles on the nearest card when released;
 * - position dots (one per possible starting card);
 * - hiding all controls when every product already fits.
 *
 * Performance: one passive scroll listener, throttled to one update per animation frame, and
 * a ResizeObserver for measurements. Everything is removed in disconnectedCallback.
 */
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class ZxRecsCarousel extends HTMLElement {
  connectedCallback() {
    this.scroller = this.querySelector('[data-scroller]');
    this.controls = this.querySelector('[data-controls]');
    if (!this.scroller || !this.controls || this.items) return;
    this.items = [...this.scroller.children];
    if (this.items.length < 2) return;

    this.dots = this.controls.querySelector('[data-dots]');
    this.onPrev = () => this.go(-1);
    this.onNext = () => this.go(1);
    this.onDot = (event) => {
      const dot = event.target.closest('[data-index]');
      if (dot) this.goTo(Number(dot.dataset.index));
    };
    this.onScroll = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.#update();
      });
    };
    this.onKey = (event) => {
      if (event.key === 'ArrowLeft') this.go(-1);
      else if (event.key === 'ArrowRight') this.go(1);
      else return;
      event.preventDefault();
    };
    this.onPointerDown = (event) => this.#dragStart(event);
    this.onPointerMove = (event) => this.#dragMove(event);
    this.onPointerUp = (event) => this.#dragEnd(event);
    this.onClick = (event) => {
      if (!this.suppressClick) return;
      this.suppressClick = false;
      event.preventDefault();
      event.stopPropagation();
    };

    this.controls.querySelector('[data-prev]')?.addEventListener('click', this.onPrev);
    this.controls.querySelector('[data-next]')?.addEventListener('click', this.onNext);
    this.dots?.addEventListener('click', this.onDot);
    this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
    this.addEventListener('keydown', this.onKey);
    this.scroller.addEventListener('pointerdown', this.onPointerDown);
    this.scroller.addEventListener('click', this.onClick, true);

    this.resizeObserver = new ResizeObserver(() => this.#measure());
    this.resizeObserver.observe(this.scroller);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.frame);
    this.#removeDragListeners();
    this.controls?.querySelector('[data-prev]')?.removeEventListener('click', this.onPrev);
    this.controls?.querySelector('[data-next]')?.removeEventListener('click', this.onNext);
    this.dots?.removeEventListener('click', this.onDot);
    this.scroller?.removeEventListener('scroll', this.onScroll);
    this.removeEventListener('keydown', this.onKey);
    this.scroller?.removeEventListener('pointerdown', this.onPointerDown);
    this.scroller?.removeEventListener('click', this.onClick, true);
  }

  /** Scroll offset that puts card `i` at the start of the row. */
  #offsetOf(i) {
    return this.items[i].offsetLeft - this.items[0].offsetLeft;
  }

  get #maxScroll() {
    return this.scroller.scrollWidth - this.scroller.clientWidth;
  }

  /** Last card that can sit at the start of the row (the ones after it are already in view). */
  get #lastStart() {
    const max = this.#maxScroll;
    for (let i = 0; i < this.items.length; i++) if (this.#offsetOf(i) >= max - 2) return i;
    return this.items.length - 1;
  }

  /** Card currently at (or nearest to) the start of the row. */
  get index() {
    const left = this.scroller.scrollLeft;
    if (left >= this.#maxScroll - 2) return this.#lastStart;
    let best = 0;
    for (let i = 1; i < this.items.length; i++) {
      if (Math.abs(this.#offsetOf(i) - left) < Math.abs(this.#offsetOf(best) - left)) best = i;
    }
    return best;
  }

  go(delta) {
    const last = this.#lastStart;
    // Step from the card being scrolled to, so quick repeated clicks each advance one card.
    const from = this.target ?? this.index;
    let next = from + delta;
    if (next > last) next = from >= last ? 0 : last; // wrap to the first card
    else if (next < 0) next = from <= 0 ? last : 0; // wrap to the last card
    this.goTo(next);
  }

  goTo(i) {
    this.target = i;
    const left = Math.min(this.#offsetOf(i), this.#maxScroll);
    this.scroller.scrollTo({ left, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    this.#setCurrent(i);
    clearTimeout(this.targetTimer);
    this.targetTimer = setTimeout(() => (this.target = undefined), 600);
  }

  #measure() {
    const overflow = this.#maxScroll > 2;
    this.controls.hidden = !overflow;
    this.classList.toggle('has-overflow', overflow);
    // Arrows level with the middle of the photos (offsetTop is relative to this element,
    // the nearest positioned ancestor).
    const media = this.items[0].querySelector('.zx-card__media');
    if (media) {
      const mid = media.offsetTop + media.offsetHeight / 2;
      this.style.setProperty('--zx-recs-mid', `${Math.round(mid)}px`);
    }
    this.#renderDots();
    this.#update();
  }

  #renderDots() {
    if (!this.dots) return;
    const count = this.#lastStart + 1;
    if (this.dots.childElementCount === count) return;
    const dots = [];
    for (let i = 0; i < count; i++) {
      const li = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'zx-recs__dot';
      button.dataset.index = String(i);
      button.setAttribute('aria-label', `Show products from ${i + 1} of ${this.items.length}`);
      li.append(button);
      dots.push(li);
    }
    this.dots.replaceChildren(...dots);
    this.shown = undefined;
  }

  #update() {
    if (this.target === undefined) this.#setCurrent(this.index);
  }

  #setCurrent(i) {
    if (i === this.shown) return;
    this.shown = i;
    this.dots?.querySelectorAll('[data-index]').forEach((dot, n) => {
      dot.setAttribute('aria-current', n === i ? 'true' : 'false');
    });
  }

  /* ---------- Mouse drag (touch uses the native scroller) ---------- */

  #dragStart(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0 || this.drag) return;
    this.suppressClick = false;
    this.drag = { id: event.pointerId, startX: event.clientX, startLeft: this.scroller.scrollLeft, active: false };
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);
  }

  #dragMove(event) {
    const drag = this.drag;
    if (!drag || event.pointerId !== drag.id) return;
    const dx = event.clientX - drag.startX;
    if (!drag.active) {
      if (Math.abs(dx) < 6) return;
      drag.active = true;
      this.classList.add('is-dragging');
    }
    event.preventDefault();
    this.scroller.scrollLeft = drag.startLeft - dx;
  }

  #dragEnd(event) {
    const drag = this.drag;
    if (!drag || event.pointerId !== drag.id) return;
    this.#removeDragListeners();
    this.drag = null;
    if (!drag.active) return;
    this.classList.remove('is-dragging');
    this.suppressClick = true; // swallow the click the drag ends with
    // Settle on the nearest card, leaning in the drag's direction.
    const dx = event.clientX - drag.startX;
    const step = this.items.length > 1 ? this.#offsetOf(1) : 1;
    let i = this.index;
    const exact = this.scroller.scrollLeft / step;
    if (Math.abs(dx) > step * 0.15) i = dx < 0 ? Math.ceil(exact - 0.001) : Math.floor(exact + 0.001);
    this.goTo(Math.max(0, Math.min(this.#lastStart, i)));
  }

  #removeDragListeners() {
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);
  }
}

if (!customElements.get('zx-recs-carousel')) customElements.define('zx-recs-carousel', ZxRecsCarousel);
